import React, { useState, useRef, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  CircularProgress,
  Box
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { keyframes } from '@emotion/react';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import NativeOrWeb from '../../utils/native';

const pulseScale = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
`;

const titleBreath = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const useStyles = makeStyles()((theme) => {
  return {
    dialogPaper: {
      borderRadius: '24px !important',
      padding: '8px 4px',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12) !important',
      width: '380px !important',
      margin: '12px',
      [theme.breakpoints.down('sm')]: {
        width: '330px !important',
        margin: '8px'
      }
    },
    dialogTitle: {
      padding: '16px 20px 8px 20px !important'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    titleText: {
      fontWeight: '600 !important',
      fontSize: '1.7rem !important',
      color: '#1e293b !important'
    },
    titleIconPrimary: {
      color: '#3966f8 !important'
    },
    recordingTitle: {
      color: '#ef4444 !important',
      animation: `${titleBreath} 2s infinite ease-in-out`
    },
    contentContainer: {
      padding: '8px 20px !important',
      overflowY: 'hidden'
    },
    introText: {
      fontSize: '1.35rem !important',
      color: '#475569 !important',
      lineHeight: '1.5 !important',
      marginBottom: '12px !important'
    },
    guideContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '8px',
      marginBottom: '12px',
      width: '100%'
    },
    guideCard: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 12px',
      borderRadius: '12px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      width: 'calc(50% - 4px)',
      boxSizing: 'border-box',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.01)',
      '&:hover': {
        borderColor: '#cbd5e1'
      }
    },
    guideCardFullWidth: {
      width: '100% !important'
    },
    guideIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.8rem !important',
      marginRight: '8px',
      flexShrink: 0
    },
    guideText: {
      fontSize: '1.35rem !important',
      fontWeight: '550 !important',
      color: '#334155 !important'
    },
    recordSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px 0',
      width: '100%'
    },
    micPulseContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      cursor: 'pointer'
    },
    micCircle: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: '#ef4444',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      zIndex: 2,
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
      transition: 'all 0.2s ease',
      '&:active': {
        transform: 'scale(0.92)'
      }
    },
    micCirclePaused: {
      backgroundColor: '#3966f8',
      boxShadow: '0 4px 12px rgba(57, 102, 248, 0.35)'
    },
    micCircleInitializing: {
      backgroundColor: '#ef4444 !important',
      opacity: '0.85 !important',
      boxShadow: 'none !important',
      cursor: 'not-allowed !important',
      '&:active': {
        transform: 'none !important'
      }
    },
    pulseRing1: {
      position: 'absolute',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      border: '2px solid rgba(239, 68, 68, 0.4)',
      animation: `${pulseScale} 2s infinite cubic-bezier(0.25, 0, 0, 1)`,
      zIndex: 1
    },
    pulseRing2: {
      position: 'absolute',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      border: '2px solid rgba(239, 68, 68, 0.2)',
      animation: `${pulseScale} 2s infinite cubic-bezier(0.25, 0, 0, 1)`,
      animationDelay: '0.6s',
      zIndex: 1
    },
    progressWrapper: {
      width: '100%',
      marginTop: '10px',
      marginBottom: '4px'
    },
    progressBar: {
      height: '6px !important',
      borderRadius: '3px !important',
      backgroundColor: '#e2e8f0 !important',
      '& .MuiLinearProgress-bar': {
        borderRadius: '3px',
        background: 'linear-gradient(90deg, #3966f8, #665eff) !important'
      }
    },
    progressBarSecondary: {
      height: '6px !important',
      borderRadius: '3px !important',
      backgroundColor: '#e2e8f0 !important',
      '& .MuiLinearProgress-bar': {
        borderRadius: '3px',
        background: 'linear-gradient(90deg, #ef4444, #f87171) !important'
      }
    },
    timeText: {
      fontSize: '1.25rem !important',
      fontWeight: '600 !important',
      color: '#64748b !important',
      marginTop: '6px'
    },
    dialogActions: {
      padding: '8px 24px 16px 24px !important',
      [theme.breakpoints.down('sm')]: {
        padding: '8px 16px 14px 16px !important'
      }
    },
    btnCancel: {
      color: '#64748b !important',
      fontSize: '1.35rem !important',
      fontWeight: '550 !important',
      textTransform: 'none !important',
      padding: '6px 12px !important',
      '&:hover': {
        backgroundColor: 'rgba(100, 116, 139, 0.06) !important'
      }
    },
    btnGradient: {
      background: 'linear-gradient(135deg, #3966f8, #665eff) !important',
      color: '#ffffff !important',
      fontSize: '1.35rem !important',
      fontWeight: '600 !important',
      borderRadius: '10px !important',
      padding: '6px 18px !important',
      textTransform: 'none !important',
      boxShadow: '0 3px 10px rgba(57, 102, 248, 0.2) !important',
      transition: 'all 0.2s ease !important',
      '&:hover': {
        boxShadow: '0 5px 14px rgba(57, 102, 248, 0.28) !important',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0)'
      },
      '&.Mui-disabled': {
        background: '#cbd5e1 !important',
        color: '#94a3b8 !important',
        boxShadow: 'none !important'
      }
    },
    btnPauseOption: {
      position: 'absolute',
      left: 'calc(50% + 40px)',
      bottom: '6px',
      borderColor: '#cbd5e1 !important',
      color: '#475569 !important',
      borderRadius: '12px !important',
      fontSize: '1.2rem !important',
      textTransform: 'none !important',
      padding: '4px 8px !important',
      height: '30px',
      fontWeight: '600 !important',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease !important',
      '&:hover': {
        borderColor: '#3966f8 !important',
        color: '#3966f8 !important',
        backgroundColor: 'rgba(57, 102, 248, 0.04) !important'
      },
      '& .MuiButton-startIcon': {
        marginRight: '2px',
        marginLeft: '-2px',
        '& svg': {
          fontSize: '16px'
        }
      },
      [theme.breakpoints.down('sm')]: {
        left: 'calc(50% + 36px)',
        fontSize: '1.15rem !important',
        padding: '3px 6px !important',
        height: '26px',
        borderRadius: '10px !important'
      }
    }
  };
});

const RecordDialog = props => {
  const {
    intl,
    title,
    content,
    timeLimit,
    recording,
    initializing,
    open,
    onClose,
    onStartRecording,
    onDoneRecording,
    style,
    ...rest
  } = props;

  const { classes } = useStyles();

  const [elapsedTime, setElapsedTime] = useState(0);
  const [paused, setPaused] = useState(false);

  const intervalRef = useRef(null);
  const elapsedTimeRef = useRef(0);

  useEffect(() => {
    if (recording && !paused) {
      intervalRef.current = setInterval(() => {
        elapsedTimeRef.current += 1;

        if (timeLimit && elapsedTimeRef.current >= timeLimit) {
          stopRecording();
        } else {
          setElapsedTime(elapsedTimeRef.current);
        }
      }, 1000);
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [recording, paused, timeLimit]);

  const startRecording = async () => {
    elapsedTimeRef.current = 0;
    setElapsedTime(0);
    setPaused(false);
    onStartRecording();
  };

  const stopRecording = async () => {
    clearInterval(intervalRef.current);
    elapsedTimeRef.current = 0;
    setElapsedTime(0);
    setPaused(false);
    onDoneRecording();
  };

  const pauseRecording = async () => {
    setPaused(true);
    await NativeOrWeb.pauseRecordingVoice();
  };

  const resumeRecording = async () => {
    setPaused(false);
    await NativeOrWeb.resumeRecordingVoice();
  };

  const formatTime = seconds => {
    let minutes = Math.floor(seconds / 60);
    let remainingSeconds = seconds % 60;

    if (minutes <= 0) {
      return `${remainingSeconds} ${
        remainingSeconds > 1 ? intl.formatMessage({ id: 'seconds' }) : intl.formatMessage({ id: 'second' })
      }`;
    } else if (remainingSeconds <= 0) {
      return `${minutes} ${minutes > 1 ? intl.formatMessage({ id: 'minutes' }) : intl.formatMessage({ id: 'minute' })}`;
    } else {
      return `${minutes} ${minutes > 1 ? intl.formatMessage({ id: 'minutes' }) : intl.formatMessage({ id: 'minute' })} 
        ${remainingSeconds} ${
        remainingSeconds > 1 ? intl.formatMessage({ id: 'seconds' }) : intl.formatMessage({ id: 'second' })
      }`;
    }
  };

  const parseContent = (text) => {
    if (!text) return { intro: '', items: [] };
    const lines = text.split('\n');
    const introLines = [];
    const items = [];
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('-') || trimmed.startsWith('*') || trimmed.startsWith('•')) {
        let cleaned = trimmed.replace(/^[-*•]\s*/, '').trim();
        if (cleaned) {
          // Replace "/" with " / " to allow wrapping and avoid horizontal overflow!
          cleaned = cleaned.replace(/\s*\/\s*/g, ' / ');
          items.push(cleaned);
        }
      } else {
        introLines.push(line);
      }
    });
    return {
      intro: introLines.join('\n').trim(),
      items
    };
  };

  const getEmoji = (item, index) => {
    const text = item.toLowerCase();
    if (text.includes('客戶') || text.includes('client') || text.includes('profile') || text.includes('資料') || text.includes('姓名') || text.includes('名稱')) {
      return '🪪';
    }
    if (text.includes('家庭') || text.includes('family') || text.includes('status')) {
      return '🏠';
    }
    if (text.includes('財務') || text.includes('financial') || text.includes('situation')) {
      return '💰';
    }
    if (text.includes('目標') || text.includes('goal') || text.includes('budget') || text.includes('預算')) {
      return '🎯';
    }
    if (text.includes('產品') || text.includes('product') || text.includes('保單') || text.includes('policy')) {
      return '📊';
    }
    if (text.includes('態度') || text.includes('attitude') || text.includes('thought')) {
      return '🤔';
    }
    if (text.includes('習慣') || text.includes('興趣') || text.includes('interest') || text.includes('habit') || text.includes('hobby')) {
      return '🎨';
    }
    if (text.includes('公司') || text.includes('insurer') || text.includes('insurance')) {
      return '🏢';
    }
    if (text.includes('計劃') || text.includes('plan') || text.includes('theme') || text.includes('主題')) {
      return '📌';
    }
    
    const fallbacks = ['🪪', '🏠', '💰', '🎯', '📊', '🤔', '🏢', '📌'];
    return fallbacks[index % fallbacks.length];
  };

  const { intro, items } = parseContent(content);

  return (
    <Dialog open={open} style={{ ...style }} slotProps={{ paper: { className: classes.dialogPaper } }} {...rest}>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleContainer}>
          <MicIcon className={recording ? classes.recordingTitle : classes.titleIconPrimary} />
          <Typography className={`${classes.titleText} ${recording ? classes.recordingTitle : ''}`}>
            {recording
              ? initializing
                ? intl.formatMessage({ id: 'recording-dialog-initializing' })
                : intl.formatMessage({ id: 'recording-dialog-recording' })
              : title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentContainer}>
        {recording ? (
          <Box className={classes.recordSection}>
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '80px', mb: 1.5 }}>
              <Box 
                className={classes.micPulseContainer} 
                onClick={initializing ? undefined : (paused ? resumeRecording : pauseRecording)}
                sx={{ cursor: initializing ? 'not-allowed' : 'pointer' }}
              >
                {!paused && !initializing && <Box className={classes.pulseRing1} />}
                {!paused && !initializing && <Box className={classes.pulseRing2} />}
                <Box 
                  className={`${classes.micCircle} ${
                    initializing ? classes.micCircleInitializing : (paused ? classes.micCirclePaused : '')
                  }`}
                >
                  {initializing ? (
                    <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                  ) : (
                    <MicIcon sx={{ fontSize: 24 }} />
                  )}
                </Box>
              </Box>

              <Button
                variant="outlined"
                className={classes.btnPauseOption}
                disabled={initializing}
                onClick={paused ? resumeRecording : pauseRecording}
                startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
              >
                {intl.formatMessage({
                  id: paused ? 'recording-dialog-resume' : 'recording-dialog-pause'
                })}
              </Button>
            </Box>
            {paused && (
              <Typography sx={{ fontSize: '1.25rem', color: '#64748b', fontWeight: 600, mb: 1 }}>
                {intl.locale.startsWith('zh') 
                  ? '已暫停錄音 (點擊繼續)' 
                  : 'Recording Paused (Tap to Resume)'}
              </Typography>
            )}
            {timeLimit && (
              <Box className={classes.progressWrapper}>
                <LinearProgress
                  variant="determinate"
                  value={(elapsedTime * 100) / timeLimit}
                  className={elapsedTime / timeLimit > 0.9 ? classes.progressBarSecondary : classes.progressBar}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography className={classes.timeText}>
                    {formatTime(elapsedTime)} / {formatTime(timeLimit)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography className={classes.introText}>{intro}</Typography>
        )}

        {items.length > 0 ? (
          <>
            {recording && (
              <Typography className={classes.introText} style={{ textAlign: 'center', marginBottom: 8, fontWeight: 500 }}>
                {intl.locale.startsWith('zh') 
                  ? '💡 語音錄製中，建議包含以下內容：' 
                  : '💡 Recording... Keep the following in mind:'}
              </Typography>
            )}
            <Box className={classes.guideContainer}>
              {items.map((item, index) => {
                const isLastOdd = items.length % 2 !== 0 && index === items.length - 1;
                return (
                  <Box 
                    key={index} 
                    className={`${classes.guideCard} ${isLastOdd ? classes.guideCardFullWidth : ''}`}
                  >
                    <Box className={classes.guideIcon}>
                      {getEmoji(item, index)}
                    </Box>
                    <Typography className={classes.guideText}>
                      {item}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          !recording && <Typography style={{ whiteSpace: 'pre-wrap' }} className={classes.introText}>{content}</Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Box
          style={{ width: '100%' }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Button className={classes.btnCancel} onClick={onClose}>
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>

          <Box>
            {recording ? (
              <Button
                className={classes.btnGradient}
                disableElevation
                disabled={initializing}
                onClick={stopRecording}
              >
                {intl.formatMessage({ id: 'recording-dialog-done' })}
              </Button>
            ) : (
              <Button className={classes.btnGradient} disableElevation onClick={startRecording}>
                {intl.formatMessage({ id: 'recording-dialog-start-recording' })}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default injectIntl(RecordDialog);
